<template>
  <component
    :is="props.fakeButton ? 'div' : 'button'"
    class="VisionIconButton"
    :class="[
      'VisionIconButton--size-' + props.size,
      'VisionIconButton--style-' + props.buttonStyle,
      'VisionIconButton--bg-colour-' + props.backgroundColour,
      'VisionIconButton--icon-colour-' + props.iconColour,
      'VisionIconButton--bg-style-' + props.buttonStyle,
      { 'VisionIconButton--hover': props.parentHover },
      { 'VisionIconButton--has-promo-theme': props.hasPromoTheme }
    ]"
  >
    <div class="VisionIconButton__icon-container">
      <VisionIcon
        :icon="props.icon"
        :colour="props.iconColour"
        :size="props.size"
        :rotate="props.rotate"
        class="VisionIconButton__icon"
      />
      <VisionIcon
        :icon="props.icon"
        :colour="props.iconColour"
        :size="props.size"
        :rotate="props.rotate"
        class="VisionIconButton__icon--duplicate"
      />
    </div>
    <div
      v-if="props.buttonStyle === 'outline' && !props.hasPromoTheme"
      class="VisionIconButton__svg-circle-container"
    >
      <svg class="VisionIconButton__svg-circle">
        <circle
          ref="animatedStroke"
          cx="50%"
          cy="50%"
          r="calc(50% - 0.5px)"
          stroke-width="1"
          fill="none"
          :style="{ '--circumference': circleCircumference }"
          class="VisionIconButton__svg-circle-inner"
        />
      </svg>
    </div>
  </component>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { IconName, IconSize, IconColour } from '../../config/icons.config'
import VisionIcon from '../VisionIcon/VisionIcon.vue'

export type BackgroundColour = 'black' | 'white' | 'dark-grey'

interface IconButtonProps {
  icon?: IconName
  size?: IconSize
  iconColour?: IconColour
  buttonStyle?: 'outline' | 'fill' | 'arrow-only' | 'none'
  backgroundColour?: BackgroundColour
  rotate?: number
  parentHover?: boolean
  fakeButton?: boolean
  hasPromoTheme?: boolean
}

const props = withDefaults(defineProps<IconButtonProps>(), {
  icon: 'arrow',
  size: 'small',
  iconColour: 'white',
  buttonStyle: 'fill',
  backgroundColour: 'black',
  rotate: 0,
  parentHover: false,
  fakeButton: false,
  hasPromoTheme: false
})

const animatedStroke = ref<object | null>(null)
const circleCircumference = ref(0)
let resizeObserver: ResizeObserver | null

function calculateLength() {
  try {
    circleCircumference.value =
      (animatedStroke.value as SVGPathElement)?.getTotalLength() + 1
  } catch {}
}

onMounted(() => {
  if (animatedStroke.value) {
    calculateLength()
    
    resizeObserver = new ResizeObserver(calculateLength)
    resizeObserver.observe(animatedStroke.value as Element)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})
</script>

<style lang="postcss">
.VisionIconButton {
  --vision-icon-button-bg-colour: var(--vision-icon-button-bg-colour);
  --vision-icon-button-icon-colour: var(--vision-icon-button-icon-colour);

  @apply rounded-full bg-off-black p-3 relative;
}

.VisionIconButton:hover,
.VisionIconButton--hover {
  .VisionIconButton__icon {
    @apply translate-x-full delay-0;

    &--duplicate {
      @apply translate-x-0 delay-150;
    }
  }
}

.VisionIconButton__icon {
  --vision-icon-fill: var(--vision-icon-button-icon-colour);
  @apply transition-transform duration-300 delay-150;

  &--duplicate {
    --vision-icon-fill: var(--vision-icon-button-icon-colour);
    @apply absolute top-0 -translate-x-full;
  }
}

.VisionIconButton__icon-container {
  @apply relative overflow-clip;
}

/**

MODIFIERS

--size-[]
--bg-colour-[]

*/

/** Size */

.VisionIconButton--size-xx-small {
  @apply p-2;
}

.VisionIconButton--size-x-small {
  @apply p-3;
}

.VisionIconButton--size-small {
  @apply p-4;
}

.VisionIconButton--size-medium {
  @apply p-5;
}

.VisionIconButton--size-large {
  @apply p-6;
}

.VisionIconButton--size-x-large {
  @apply p-8;
}

.VisionIconButton--size-stretch {
  padding: 25%;
}

/** Background Colour */

.VisionIconButton--bg-colour-white {
  background-color: var(--vision-icon-button-bg-colour, theme('colors.white'));
}

.VisionIconButton--bg-colour-black {
  background-color: var(
    --vision-icon-button-bg-colour,
    theme('colors.off-black')
  );
}

.VisionIconButton--bg-colour-dark-grey {
  background-color: var(
    --vision-icon-button-bg-colour,
    theme('colors.grey.800')
  );
}

/** Icon Colour */
.VisionIconButton--icon-colour-white {
  --icon-colour: theme(colors.white);
}

.VisionIconButton--icon-colour-black {
  --icon-colour: theme(colors.black);
}

/** Outline */
.VisionIconButton--bg-style-outline {
  @apply relative bg-transparent;

  &::after {
    @apply content-[''] absolute inset-0
    border rounded-full transition-opacity duration-300 delay-[400ms];
    border-color: var(--icon-colour);
  }
}

.VisionIconButton--bg-style-outline:hover,
.VisionIconButton--bg-style-outline.VisionIconButton--hover {
  &::after {
    @apply opacity-40 delay-0;
  }

  .VisionIconButton__svg-circle-inner {
    stroke-dashoffset: 0;
  }
}

.VisionIconButton__svg-circle-container {
  @apply absolute inset-0 z-[2];
}

.VisionIconButton__svg-circle {
  @apply w-full h-full;
}

.VisionIconButton__svg-circle-inner {
  stroke: var(--icon-colour);
  stroke-dashoffset: var(--circumference);
  stroke-dasharray: var(--circumference);
  transition: stroke-dashoffset 0.5s ease-in-out;
}

@keyframes circle-stroke-animation {
  from {
    stroke-dashoffset: var(--initial-dashoffset);
  }
  to {
    stroke-dashoffset: 0;
  }
}

.VisionIconButton--has-promo-theme {
  --vision-icon-fill: var(--promo-highlight, theme(colors.blue));
  --vision-icon-button-icon-colour: var(--promo-highlight, theme(colors.blue));
  @apply promo-gradient-border-single promo-gradient-border-animated
  border-transparent transition-all duration-500;
  box-shadow: 0px 0px 0px var(--promo-highlight),
    inset 0px 0px 0px var(--promo-highlight),
    inset 0px 0px 0px var(--promo-key);

  &::after {
    @apply hidden;
  }

  &:hover,
  &.VisionIconButton--hover {
    border-color: var(--promo-highlight);
    box-shadow: 0px 0px 2px var(--promo-highlight),
      inset 0px 0px 4px var(--promo-highlight),
      inset 0px 0px 1px var(--promo-key);
  }
}

/**
  Button Style
 */

.VisionIconButton--style-arrow-only {
  @apply bg-transparent;
}
</style>
